import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MacroSeries {
  series_id: string;
  name: string;
  category: string;
  frequency: string;
  country: string;
  description: string | null;
  parent_series_id: string | null;
  related_series: string[];
}

export interface MacroDataPoint {
  date: string;   // YYYY-MM-DD
  value: number;
  unit: string | null;
}

export interface PinnedSeries {
  seriesId: string;
  color: string;
  visible: boolean;
}

// ── Color palette (10 distinct colors) ───────────────────────────────────────

export const MACRO_COLORS = [
  '#f59e0b', // amber
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f43f5e', // rose
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#ec4899', // pink
  '#14b8a6', // teal
];

function nextColor(pinnedSeries: PinnedSeries[]): string {
  const usedColors = new Set(pinnedSeries.map((s) => s.color));
  const available = MACRO_COLORS.find((c) => !usedColors.has(c));
  return available ?? MACRO_COLORS[pinnedSeries.length % MACRO_COLORS.length];
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface MacroState {
  // Persisted: which series the user has pinned
  pinnedSeries: PinnedSeries[];

  // Runtime cache: series metadata fetched from API
  seriesMeta: MacroSeries[];
  metaLoaded: boolean;
  metaLoading: boolean;

  // Runtime cache: time-series data per series_id
  seriesData: Record<string, MacroDataPoint[]>;
  loadingIds: Set<string>;

  // Pearson correlation per series_id (computed against current symbol close prices)
  correlations: Record<string, number | null>;

  // Detail modal state
  detailSeriesId: string | null;
  setDetailSeriesId: (id: string | null) => void;

  // Actions
  addSeries: (seriesId: string) => void;
  removeSeries: (seriesId: string) => void;
  toggleVisibility: (seriesId: string) => void;
  setSeriesMeta: (meta: MacroSeries[]) => void;
  setMetaLoading: (loading: boolean) => void;
  setSeriesData: (seriesId: string, data: MacroDataPoint[]) => void;
  setLoadingId: (id: string, loading: boolean) => void;
  setCorrelation: (seriesId: string, rho: number | null) => void;
  clearAll: () => void;
}

export const useMacroStore = create<MacroState>()(
  persist(
    (set, get) => ({
      // Persisted
      pinnedSeries: [],

      // Runtime
      seriesMeta: [],
      metaLoaded: false,
      metaLoading: false,
      seriesData: {},
      loadingIds: new Set(),
      correlations: {},
      detailSeriesId: null,

      setDetailSeriesId: (id) => set({ detailSeriesId: id }),

      addSeries: (seriesId) => {
        const { pinnedSeries } = get();
        if (pinnedSeries.some((s) => s.seriesId === seriesId)) return;
        set({
          pinnedSeries: [
            ...pinnedSeries,
            { seriesId, color: nextColor(pinnedSeries), visible: true },
          ],
        });
      },

      removeSeries: (seriesId) => {
        set((state) => {
          const next = { ...state.seriesData };
          delete next[seriesId];
          return {
            pinnedSeries: state.pinnedSeries.filter((s) => s.seriesId !== seriesId),
            seriesData: next,
          };
        });
      },

      toggleVisibility: (seriesId) => {
        set((state) => ({
          pinnedSeries: state.pinnedSeries.map((s) =>
            s.seriesId === seriesId ? { ...s, visible: !s.visible } : s,
          ),
        }));
      },

      setSeriesMeta: (meta) => set({ seriesMeta: meta, metaLoaded: true }),
      setMetaLoading: (loading) => set({ metaLoading: loading }),

      setSeriesData: (seriesId, data) => {
        set((state) => ({
          seriesData: { ...state.seriesData, [seriesId]: data },
        }));
      },

      setCorrelation: (seriesId, rho) => {
        set((state) => ({
          correlations: { ...state.correlations, [seriesId]: rho },
        }));
      },

      setLoadingId: (id, loading) => {
        set((state) => {
          const next = new Set(state.loadingIds);
          if (loading) next.add(id);
          else next.delete(id);
          return { loadingIds: next };
        });
      },

      clearAll: () => set({ pinnedSeries: [], seriesData: {}, loadingIds: new Set() }),
    }),
    {
      name: 'cseview-macro-store',
      partialize: (state) => ({ pinnedSeries: state.pinnedSeries }),
    },
  ),
);
